import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Download,
  Eye,
  FileText,
  Loader2,
  Trash2,
  Upload,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { useOssAuth } from "../../context/OssAuthContext";
import OssSectionPageHeader from "../../components/oss/OssSectionPageHeader";
import { OSS_PERSONALE_DOC_TYPES } from "../../config/ossPersonaleDocumenti";
import { saveOssBlob } from "../../lib/oss/bridge";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("it-IT");
}

function formatSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function OssDocumenti() {
  const { token } = useOssAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [activeType, setActiveType] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchDocs = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/oss/me/documenti`, {
        headers: authHeaders,
      });
      setDocuments(data.documents || []);
    } catch {
      toast.error("Errore nel caricamento dei documenti");
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    fetchDocs();
  }, [fetchDocs]);

  const docsForType = (type) => documents.filter((d) => d.doc_type === type);
  const countForType = (type) => docsForType(type).length;

  const openType = (key) => {
    setActiveType(key);
    fetchDocs();
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeType) return;
    if (file.size > 50 * 1024 * 1024) {
      toast.error("File troppo grande (max 50 MB)");
      e.target.value = "";
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("descrizione", file.name || "");
    try {
      await axios.post(`${API_URL}/api/oss/me/documenti/${activeType}`, fd, {
        headers: { ...authHeaders, "Content-Type": "multipart/form-data" },
      });
      toast.success("Documento caricato");
      await fetchDocs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel caricamento");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const fetchBlob = async (fileId) => {
    const { data } = await axios.get(
      `${API_URL}/api/oss/me/documenti/${fileId}/file`,
      { headers: authHeaders, responseType: "blob" }
    );
    return data;
  };

  const handlePreview = async (doc) => {
    try {
      const blob = await fetchBlob(doc.file_id);
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch {
      toast.error("Impossibile aprire il documento");
    }
  };

  const handleDownload = async (doc) => {
    try {
      const blob = await fetchBlob(doc.file_id);
      await saveOssBlob(blob, doc.file_name || "documento");
    } catch {
      toast.error("Impossibile scaricare il documento");
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Eliminare "${doc.file_name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/oss/me/documenti/${doc.file_id}`, {
        headers: authHeaders,
      });
      toast.success("Documento eliminato");
      await fetchDocs();
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const activeMeta = OSS_PERSONALE_DOC_TYPES.find((t) => t.key === activeType);
  const modalDocs = activeType ? docsForType(activeType) : [];

  return (
    <div>
      <OssSectionPageHeader
        sectionKey="documenti"
        title="I miei documenti"
        subtitle="Carica attestati, corsi, BLSD, HACCP e certificati. Sono visibili anche alla struttura in Personale."
      />

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {OSS_PERSONALE_DOC_TYPES.map(({ key, label, description, Icon, iconWrap }) => {
            const n = countForType(key);
            return (
              <button
                key={key}
                type="button"
                onClick={() => openType(key)}
                className="text-left bg-white rounded-2xl border border-slate-100 p-5 shadow-sm hover:shadow-md hover:border-slate-200 transition-all touch-manipulation"
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center ring-1 shrink-0 ${iconWrap}`}
                  >
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold text-slate-900">{label}</p>
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          n > 0
                            ? "bg-emerald-50 text-emerald-700"
                            : "bg-slate-100 text-slate-500"
                        }`}
                      >
                        {n}
                      </span>
                    </div>
                    <p className="text-sm text-slate-500 mt-1 leading-snug">{description}</p>
                    <p className="text-xs text-amber-700 mt-2 font-medium flex items-center gap-1">
                      <Upload className="w-3.5 h-3.5" />
                      Tocca per caricare
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <Dialog
        open={!!activeType}
        onOpenChange={(open) => {
          if (!open) setActiveType(null);
        }}
      >
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{activeMeta?.label || "Documenti"}</DialogTitle>
            <DialogDescription>
              {activeMeta?.description}. Formati ammessi: PDF, immagini, Word, Excel (max 50 MB).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png,.gif,.heic,.heif,.doc,.docx,.xls,.xlsx"
              onChange={handleUpload}
            />
            <Button
              type="button"
              className="w-full bg-amber-600 hover:bg-amber-700 min-h-[48px]"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Carica file
            </Button>

            {modalDocs.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center">
                <FileText className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">Nessun documento caricato</p>
              </div>
            ) : (
              <ul className="space-y-2">
                {modalDocs.map((doc) => (
                  <li
                    key={doc.file_id}
                    className="flex items-center gap-2 rounded-xl border border-slate-100 bg-white p-3"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-800 truncate">
                        {doc.file_name}
                      </p>
                      <p className="text-xs text-slate-500">
                        {formatDate(doc.uploaded_at)}
                        {doc.file_size ? ` · ${formatSize(doc.file_size)}` : ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-0.5 shrink-0">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-11 w-11"
                        title="Anteprima"
                        aria-label="Anteprima"
                        onClick={() => handlePreview(doc)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-11 w-11"
                        title="Scarica"
                        aria-label="Scarica"
                        onClick={() => handleDownload(doc)}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-11 w-11 text-red-600 hover:bg-red-50"
                        title="Elimina"
                        aria-label="Elimina"
                        onClick={() => handleDelete(doc)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!previewUrl}
        onOpenChange={(open) => {
          if (!open && previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
          }
        }}
      >
        <DialogContent className="max-w-3xl h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Anteprima</DialogTitle>
          </DialogHeader>
          {previewUrl && (
            <iframe title="Anteprima documento" src={previewUrl} className="flex-1 w-full rounded-lg border" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
